// Accueil du module pro et bloc du tableau de bord.
import { Link } from "react-router";
import { useGet } from "@core/hooks";
import { useOrg } from "@core/org";
import { Card, ErrorBox, Loading, Notice, PageHeader, Stat } from "@core/ui";

const SECTIONS: { to: string; title: string; text: string; key?: string }[] = [
  { to: "professionnels/equipe", title: "Équipe", text: "Équipes, portefeuilles attribués, tâches et charge de travail.", key: "teams" },
  { to: "professionnels/mandants", title: "Propriétaires mandants", text: "Portail en lecture pour chaque propriétaire ou SCI.", key: "owners" },
  { to: "professionnels/depenses", title: "Validations de dépenses", text: "Seuils par mandant, accord par portail ou lien, journal.", key: "pending_approvals" },
  { to: "professionnels/actions-groupees", title: "Actions groupées", text: "Messages et documents à plusieurs locataires, avec aperçu." },
  { to: "professionnels/rapports", title: "Rapports", text: "Rapport mensuel PDF par mandant, partagé sur son portail." },
  { to: "professionnels/artisans", title: "Artisans", text: "Métiers, zones, attestation décennale et interventions.", key: "contractors" },
  { to: "professionnels/import", title: "Import", text: "Mandants, logements, baux et locataires depuis un fichier CSV." },
  { to: "professionnels/integrations", title: "Intégrations (API)", text: "Jetons d’API en lecture et webhooks signés." },
  { to: "professionnels/marque", title: "Marque", text: "Logo, couleur accessible et coordonnées de support." },
  { to: "professionnels/baux-commerciaux", title: "Baux commerciaux", text: "Régimes commercial (3-6-9) et professionnel, échéances, indexation.", key: "commercial_leases" },
  { to: "professionnels/facturation", title: "Facturation", text: "Factures et avoirs des loyers commerciaux, distincts des quittances." },
];

export function ProHomePage() {
  const org = useOrg();
  const q = useGet<any>(org.api("/pro/overview"));
  const o = q.data;
  return (
    <div className="page">
      <PageHeader
        eyebrow="Professionnels"
        title="Outils pour les portefeuilles gérés"
        intro="Ces fonctions visent surtout les espaces « professionnel » : agences, gestionnaires et SCI qui gèrent des biens pour le compte de plusieurs propriétaires. Elles restent disponibles pour tout espace."
      />
      <ErrorBox error={q.error} />
      {q.isLoading ? <Loading /> : null}
      {o && !o.professional ? (
        <Notice title="Votre espace est de type particulier">
          Vous pouvez utiliser l’annuaire des artisans, les baux commerciaux ou l’import. Les équipes, le portail des mandants et les rapports prennent tout leur sens avec plusieurs propriétaires.
        </Notice>
      ) : null}
      {o?.restricted_scope ? <Notice title="Périmètre restreint">Vous voyez uniquement les logements qui vous sont attribués.</Notice> : null}
      {o ? (
        <div className="stats">
          <Stat label="Accords de dépense attendus" value={o.pending_approvals} />
          <Stat label="Échéances de bail sous 6 mois" value={o.upcoming_deadlines} />
          <Stat label="Attestations d’artisans à vérifier" value={o.insurance_to_check} />
          <Stat label="Mes tâches ouvertes" value={o.my_open_tasks} />
        </div>
      ) : null}
      <div className="pro-links">
        {SECTIONS.map((s) => (
          <Link key={s.to} to={org.to(s.to)} className="pro-link">
            <strong>
              {s.title}
              {s.key && o ? ` · ${o[s.key]}` : ""}
            </strong>
            <span className="small">{s.text}</span>
          </Link>
        ))}
      </div>
      <Card title="Ce que ces outils ne font pas">
        <ul>
          <li>Ils ne remplacent ni un mandat de gestion ni un conseil juridique : les règles des baux commerciaux et la facturation restent à valider.</li>
          <li>Un accord de dépense n’est ni la signature d’un devis ni un paiement.</li>
          <li>Un mandant ne voit jamais les dossiers des candidats ni les notes internes de l’équipe.</li>
        </ul>
      </Card>
    </div>
  );
}

/** Bloc du tableau de bord : affiché pour un espace professionnel ou dès qu'un point demande attention. */
export function ProDashboard({ orgId }: { orgId: string }) {
  const q = useGet<any>(`/api/v1/orgs/${orgId}/pro/overview`);
  const o = q.data;
  if (!o) return null;
  const attention = o.pending_approvals + o.upcoming_deadlines + o.insurance_to_check;
  if (!o.professional && attention === 0) return null;
  return (
    <Card title="Professionnels">
      <ul className="list-plain">
        <li>
          <Link to={`/espace/${orgId}/professionnels/depenses`}>{o.pending_approvals} accord(s) de dépense attendu(s)</Link>
        </li>
        <li>
          <Link to={`/espace/${orgId}/professionnels/baux-commerciaux`}>{o.upcoming_deadlines} échéance(s) de bail sous 6 mois</Link>
        </li>
        <li>
          <Link to={`/espace/${orgId}/professionnels/artisans`}>{o.insurance_to_check} attestation(s) d’artisan à vérifier</Link>
        </li>
      </ul>
    </Card>
  );
}
